/**
 * DealAtendimentos — aba "Atendimentos" do painel do negócio.
 *
 * Lista os atendimentos (conversas) do lead principal, um card por atendimento,
 * no mesmo formato dos cards da aba Negócios: canal, status (aberto/encerrado),
 * responsável, datas e prévia da última mensagem. O atendimento em si abre no
 * módulo Atendimento ("Abrir conversa").
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Icon from '../../components/icons.jsx'
import { atListConversations } from '../../db/api.js'
import { CHANNEL_META } from '../atendimento/atendimentoKit.jsx'
import { fmtDateTime } from './panelKit.jsx'

const STATUS = {
  aberta: { label: 'Aberto', color: '#30d158' },
  encerrada: { label: 'Encerrado', color: '#8e8e93' },
  arquivada: { label: 'Arquivado', color: '#9e9e9e' },
}

export default function DealAtendimentos({ contactId, dealId, members = [] }) {
  const navigate = useNavigate()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)

  useEffect(() => {
    let alive = true
    setRows([]); setErr(null)
    if (!contactId) { setLoading(false); return undefined }
    setLoading(true)
    // A lista padrão esconde arquivadas; busca as duas e junta.
    Promise.all([
      atListConversations({ contactId }),
      atListConversations({ contactId, filtro: 'arquivadas' }).catch(() => []),
    ])
      .then(([active, archived]) => {
        if (!alive) return
        const byId = new Map([...active, ...archived].map((c) => [c.id, c]))
        setRows([...byId.values()].sort((a, b) => Date.parse(b.last_message_at || b.created_at || 0) - Date.parse(a.last_message_at || a.created_at || 0)))
      })
      .catch((e) => alive && setErr(e.message))
      .finally(() => alive && setLoading(false))
    return () => { alive = false }
  }, [contactId])

  if (!contactId) return <div className="dp-at-empty">Nenhum lead vinculado para trazer os atendimentos.</div>
  if (loading) return <div className="dp-at-empty">Carregando…</div>
  if (err) return <div className="fc-error">{err}</div>
  if (!rows.length) {
    return <div className="dp-at-empty">Nenhum atendimento registrado.<small>As conversas deste lead no módulo Atendimento aparecerão aqui.</small></div>
  }

  return (
    <div className="dp-at-list">
      {rows.map((c) => {
        const channel = CHANNEL_META[c.channel]
        const status = STATUS[c.status] || { label: c.status || '—', color: '#8e8e93' }
        const owner = members.find((m) => m.id === c.assigned_to)?.nome
        return (
          <div key={c.id} className="dp-at-card" role="button" tabIndex={0}
            title="Abrir no módulo Atendimento"
            onClick={() => navigate(`/atendimento?conversation=${c.id}`)}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/atendimento?conversation=${c.id}`) }}>
            <div className="dp-at-main">
              <div className="dp-at-title">
                {channel && <Icon n={channel.icon} s={14} />} Atendimento via {channel?.label || c.channel}
                {c.deal_id && c.deal_id === dealId && <span className="dp-at-tag">este negócio</span>}
              </div>
              <div className="dp-at-meta">
                <span><Icon n="calendar" s={12} /> Iniciado em {fmtDateTime(c.created_at)}</span>
                {c.last_message_at && <span><Icon n="clock" s={12} /> Última mensagem em {fmtDateTime(c.last_message_at)}</span>}
                <span><Icon n="user" s={12} /> {owner ? `Atendente: ${owner}` : 'Sem atendente'}</span>
              </div>
              {c.last_message_preview && <div className="dp-at-preview">{c.last_message_preview}</div>}
            </div>
            <div className="dp-at-side">
              <span className="dp-at-status" style={{ color: status.color, borderColor: status.color }}>{status.label}</span>
              <Icon n="chevron_r" s={16} />
            </div>
          </div>
        )
      })}
    </div>
  )
}
